// client/src/components/pages/LexiStar/Declarations/Declarations.jsx
//
// LexiStar declarations (ST-00188, M3-U1; T4-S14; LS-09 §5.21). Route
// /lexistar/declarations, page permission lexistar.declarations.manage.
//
// One place to declare a condition across many trips — a closure, a protest,
// a weighbridge outage — for a period in the past or the future. The list
// shows each declaration with its effect ("23 trips · 41 delay hours
// reclassified · 7 deviations treated as approved"), whether it is active,
// scheduled or over, and lets a published one be retracted (future → the
// engine stops applying it; past → a compensating recompute). Security and
// risk events are never touched, by design.

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { FaBullhorn, FaPlus, FaSyncAlt, FaShieldAlt } from 'react-icons/fa';
import SegmentedControl from '../../../common/SegmentedControl';
import { useAuth } from '../../../../context/AuthContext';
import {
  listDeclarations,
  retractDeclaration,
  pickList,
} from '../../../../services/lexistarAssurance';
import {
  PageHeader,
  ErrorBanner,
  NoticeBanner,
  LoadingState,
  EmptyState,
  NoPermissionPage,
  cardClasses,
} from '../StatusBoard/atoms';
import { fmtDateTime } from '../StatusBoard/format';
import { Modal, ReasonField, InlineError, buttonClasses } from '../ReviewQueue/assuranceAtoms';
import { errorMessage } from '../ReviewQueue/assuranceShared';
import {
  DECLARATION_STATE_META,
  effectMeta,
  effectSentence,
  scopeText,
  liveness,
} from './declarationLogic';
import DeclarationForm, { SECURITY_NOTE } from './DeclarationForm';
import useScopeReference from './useScopeReference';

const FILTERS = [
  { value: 'published', label: 'Published' },
  { value: 'draft', label: 'Drafts' },
  { value: 'retracted', label: 'Retracted' },
  { value: 'all', label: 'All' },
];

const LIVENESS_TEXT = { active: 'In force now', scheduled: 'Scheduled', ended: 'Period over' };

function StateChip({ state }) {
  const meta = DECLARATION_STATE_META[state] || DECLARATION_STATE_META.draft;
  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold ${meta.className}`}
    >
      <span aria-hidden="true">{meta.glyph}</span>
      {meta.label}
    </span>
  );
}

function DeclarationCard({ d, names, canManage, onRetract }) {
  const live = liveness(d);
  const summary = effectSentence(d.effect_summary || d.preview, d.effect);
  return (
    <li className={`${cardClasses} p-4`} aria-label={`${effectMeta(d.effect).label}: ${d.reason}`}>
      <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-3">
        <div className="min-w-0 space-y-1">
          <div className="flex flex-wrap items-center gap-2">
            <StateChip state={d.state} />
            {live && (
              <span className="text-xs font-medium text-slate-700">{LIVENESS_TEXT[live]}</span>
            )}
            <span className="text-sm font-semibold text-slate-900">
              {effectMeta(d.effect).label}
            </span>
          </div>
          <p className="text-sm text-slate-800">{d.reason}</p>
          <p className="text-xs text-slate-600">
            {scopeText(d.scope, names)} · {fmtDateTime(d.valid_from)} → {fmtDateTime(d.valid_to)}
            {d.reason_code && <> · {d.reason_code.replace(/_/g, ' ')}</>}
            {d.alt_corridor && ' · with an alternate corridor'}
          </p>
          {summary && (
            <p className="text-sm font-semibold text-slate-900 tabular-nums">{summary}</p>
          )}
          <p className="text-xs text-slate-500">
            {d.published_by && (
              <>
                Published by {d.published_by} · {fmtDateTime(d.published_at)}
              </>
            )}
            {d.retracted_by && (
              <>
                {' '}
                · retracted by {d.retracted_by} · {fmtDateTime(d.retracted_at)}
              </>
            )}
            {d.recompute_job_id && (
              <>
                {' · '}
                <Link to={`/lexistar/recompute-jobs/${d.recompute_job_id}`} className="underline">
                  its recompute
                </Link>
              </>
            )}
          </p>
        </div>
        {canManage && d.state === 'published' && (
          <button type="button" onClick={() => onRetract(d)} className={buttonClasses.secondary}>
            Retract…
          </button>
        )}
      </div>
    </li>
  );
}

const Declarations = () => {
  const { hasPermission } = useAuth();
  const canManage = hasPermission('lexistar.declarations.manage');
  const [searchParams, setSearchParams] = useSearchParams();
  const filter = FILTERS.some((f) => f.value === searchParams.get('show'))
    ? searchParams.get('show')
    : 'published';
  const creating = searchParams.get('new') === '1';
  const reference = useScopeReference();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  const [retracting, setRetracting] = useState(null);
  const [retractReason, setRetractReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [retractError, setRetractError] = useState(null);

  const setParams = useCallback(
    (changes) => {
      const next = new URLSearchParams(searchParams);
      Object.entries(changes).forEach(([k, v]) => (v ? next.set(k, v) : next.delete(k)));
      setSearchParams(next, { replace: true });
    },
    [searchParams, setSearchParams]
  );

  const load = useCallback(
    async ({ quiet = false } = {}) => {
      if (!canManage) return;
      try {
        if (!quiet) setLoading(true);
        const res = await listDeclarations({ state: filter === 'all' ? undefined : filter });
        const list = pickList(res, 'declarations');
        setRows([...list].sort((a, b) => String(b.valid_from).localeCompare(String(a.valid_from))));
        setError(null);
      } catch (err) {
        if (!quiet) setError(errorMessage(err, 'Failed to load declarations.'));
      } finally {
        if (!quiet) setLoading(false);
      }
    },
    [canManage, filter]
  );

  useEffect(() => {
    load();
  }, [load]);

  const ordered = useMemo(() => {
    const rank = { active: 0, scheduled: 1, ended: 2 };
    return [...rows].sort((a, b) => (rank[liveness(a)] ?? 3) - (rank[liveness(b)] ?? 3));
  }, [rows]);

  const retract = async () => {
    setBusy(true);
    setRetractError(null);
    try {
      await retractDeclaration(retracting.id, { reason: retractReason.trim() || undefined });
      setNotice(
        new Date(retracting.valid_from) > new Date()
          ? 'Retracted. The engine stops applying it.'
          : 'Retracted. A recompute of the affected period was queued; a second user publishes it.'
      );
      setRetracting(null);
      await load({ quiet: true });
    } catch (err) {
      setRetractError(errorMessage(err, 'The declaration was not retracted.'));
    } finally {
      setBusy(false);
    }
  };

  const header = (
    <PageHeader
      icon={FaBullhorn}
      title="Declarations"
      subtitle="A closure or outage, declared once for every trip."
      toolbar={
        canManage && !creating ? (
          <SegmentedControl
            options={FILTERS}
            value={filter}
            onChange={(v) => setParams({ show: v === 'published' ? '' : v })}
            ariaLabel="Declarations"
          />
        ) : null
      }
      actions={
        canManage ? (
          <div className="flex items-center gap-2">
            {!creating && (
              <>
                <button
                  type="button"
                  onClick={() => load()}
                  aria-label="Refresh"
                  className="inline-flex items-center px-3 py-2 text-sm text-slate-700 bg-surface border border-slate-200 rounded-lg hover:bg-slate-50"
                >
                  <FaSyncAlt size={12} className={loading ? 'animate-spin' : ''} />
                </button>
                <button
                  type="button"
                  onClick={() => setParams({ new: '1' })}
                  className={buttonClasses.primary}
                >
                  <FaPlus size={11} /> <span className="hidden sm:inline">New declaration</span>
                </button>
              </>
            )}
          </div>
        ) : null
      }
    />
  );

  if (!canManage) {
    return (
      <NoPermissionPage
        header={header}
        message="You do not have permission to manage declarations."
      />
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {header}
      <div className="max-w-[1500px] mx-auto px-3 sm:px-6 py-4 sm:py-6 space-y-4">
        <ErrorBanner message={error} onDismiss={() => setError(null)} />
        <NoticeBanner message={notice} onDismiss={() => setNotice(null)} />

        {creating ? (
          <DeclarationForm
            reference={reference}
            onCancel={() => setParams({ new: '' })}
            onPublished={({ recomputeJobId, when }) => {
              setParams({ new: '', show: '' });
              setNotice(
                when === 'future'
                  ? 'Published. It applies to trips as they happen.'
                  : `Published.${recomputeJobId ? ' A recompute of the past period was queued' : ' The past period will be recomputed'}; a second user publishes it.`
              );
              load({ quiet: true });
            }}
          />
        ) : (
          <>
            <p className="flex items-center gap-2 text-sm text-slate-600">
              <FaShieldAlt className="text-slate-500" aria-hidden="true" />
              {SECURITY_NOTE}
            </p>
            {loading && rows.length === 0 ? (
              <LoadingState label="Loading declarations..." />
            ) : ordered.length === 0 ? (
              <div className={cardClasses}>
                <EmptyState icon={FaBullhorn} title="No declarations here">
                  Declare a road closure, protest or weighbridge outage once instead of approving
                  each trip’s deviation or delay by hand.
                </EmptyState>
              </div>
            ) : (
              <ul className="space-y-3" aria-label="Declarations">
                {ordered.map((d) => (
                  <DeclarationCard
                    key={d.id}
                    d={d}
                    names={reference.names}
                    canManage={canManage}
                    onRetract={(x) => {
                      setRetractReason('');
                      setRetractError(null);
                      setRetracting(x);
                    }}
                  />
                ))}
              </ul>
            )}
          </>
        )}
      </div>

      <Modal
        open={!!retracting}
        title="Retract this declaration"
        onClose={() => setRetracting(null)}
        footer={
          <>
            <button
              type="button"
              onClick={() => setRetracting(null)}
              className={buttonClasses.secondary}
            >
              Keep it
            </button>
            <button
              type="button"
              onClick={retract}
              disabled={busy}
              className={buttonClasses.danger}
            >
              {busy ? 'Retracting…' : 'Retract'}
            </button>
          </>
        }
      >
        {retracting && (
          <>
            <p className="text-sm text-slate-700">
              “{retracting.reason}” — {effectMeta(retracting.effect).label.toLowerCase()}.
            </p>
            <p className="text-sm text-slate-600">
              {new Date(retracting.valid_from) > new Date()
                ? 'It has not started: the engine simply stops applying it.'
                : 'Part of it is in the past: a compensating recompute is queued, and trips return to how they were classified before once a second user publishes it.'}
            </p>
            <ReasonField value={retractReason} onChange={setRetractReason} required={false} />
            <InlineError message={retractError} />
          </>
        )}
      </Modal>
    </div>
  );
};

export default Declarations;
